import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { MyTasksetService } from '@sbd-doug-frontend/app/modules/my-tasks/my-taskset.service';
import { Account, AccountUserListItem } from '@sbd-doug-frontend/shared/lib/types/account.types';
import {
  TaskCreateFormComponent,
  TaskCreateFormDraft,
} from '@sbd-doug-frontend/shared/lib/shared-components/task-viewer/content/right-content/stage-column/forms/task-create-form/task-create-form.component';
import { CIPXDrawerContentInterface } from '@sbd-doug-frontend/shared/lib/ui/drawer/drawer-content.interface';
import { DRAWER_DATA, DRAWER_REF, DrawerRef } from '@sbd-doug-frontend/shared/lib/ui/drawer/drawer-manager.service';
import { PersistentDrawerTabsService } from '@sbd-doug-frontend/app/layout/services/persistent-drawer-tabs.service';
import { distinctUntilChanged, map, Subject, takeUntil } from 'rxjs';
import { CommonDrawerFooterComponent } from '@sbd-doug-frontend/shared/lib/shared-components/common-drawer-footer/common-drawer-footer.component';
import { toObservable } from '@angular/core/rxjs-interop';

export interface AccountManagerActivityFollowUpTaskDrawerData {
  tabId?: string;
  accountId?: number | null;
  accountLabel?: string | null;
  accountUsers?: AccountUserListItem[];
  taskDraft?: TaskCreateFormDraft | null;
  title?: string;
}

@Component({
  selector: 'sbd-doug-frontend-account-manager-activity-follow-up-task-drawer-wrapper',
  standalone: true,
  imports: [CommonModule, RouterModule, CommonDrawerFooterComponent, TaskCreateFormComponent],
  templateUrl: './account-manager-activity-follow-up-task-drawer-wrapper.component.html',
  styleUrl: './account-manager-activity-drawer-wrapper.component.scss',
})
export class AccountManagerActivityFollowUpTaskDrawerWrapperComponent
  implements CIPXDrawerContentInterface<AccountManagerActivityFollowUpTaskDrawerData>, OnInit, OnDestroy
{
  @Output() closeRequested = new EventEmitter<void>();
  @Output() drawerClosed = new EventEmitter<void>();
  @Output() drawerCollapsed = new EventEmitter<void>();

  _taskSetService = inject(MyTasksetService);
  _persistentDrawerTabsService = inject(PersistentDrawerTabsService);
  data = inject(DRAWER_DATA, { optional: true }) as AccountManagerActivityFollowUpTaskDrawerData | null;
  drawerRef = inject(DRAWER_REF, {
    optional: true,
  }) as DrawerRef<AccountManagerActivityFollowUpTaskDrawerWrapperComponent> | null;
  selectedAccount: Account = {} as Account;
  taskDraft: TaskCreateFormDraft | null = null;
  componentSelf = this;
  @ViewChild(TaskCreateFormComponent) taskCreateFormComponent?: TaskCreateFormComponent;
  drawerFooterButtons = [
    { id: 'save', label: 'Create', color: 'primary' as const, enabled: true, visible: true, action: 'submitTask' },
    { id: 'cancel', label: 'Cancel', color: '' as const, enabled: true, visible: true, action: 'closeDrawer' },
  ];
  private _dismissAction: 'close' | 'collapse' = 'close';
  private _tabs$ = toObservable(this._persistentDrawerTabsService.tabs);
  private _unsubscribeAll = new Subject<void>();

  ngOnInit(): void {
    this.drawerRef?.setTitle(this.data?.title || 'Create Follow-up Task');
    this.drawerRef?.setShowBackButton(false);
    this.drawerRef?.setShowCollapseButton(true);
    this._taskSetService.scope.set('account_tasks');
    this._taskSetService.accountId.set(this.accountId ?? 0);
    this._taskSetService.newTaskStoredState.set(null);
    this.selectedAccount = this._buildSelectedAccount();
    this.taskDraft = this.data?.taskDraft ?? null;
    this._attachDraftSyncListener();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  canClose(): boolean {
    return true;
  }

  beforeDrawerClosed(): void {
    if (this._dismissAction === 'collapse') {
      this.drawerCollapsed.emit();
    } else {
      this.drawerClosed.emit();
    }
    this._dismissAction = 'close';
  }

  closeDrawer(): void {
    this.closeRequested.emit();
  }

  onCollapseClick(): void {
    this._dismissAction = 'collapse';
  }

  onTaskDraftChanged(draft: TaskCreateFormDraft): void {
    const nextDraft = this._hydrateTaskDraft(draft);
    if (this._draftEquals(this.taskDraft, nextDraft)) {
      return;
    }

    this.taskDraft = nextDraft;
    this._persistDraftToTabContext();
  }

  onTaskCreateFormClose(): void {
    this._clearDraftFromTabContext();
    this._taskSetService.newTaskStoredState.set(null);
    this.closeDrawer();
  }

  submitTask(): void {
    this.taskCreateFormComponent?.submitForm();
  }

  get accountId(): number | null {
    return this.data?.accountId ?? null;
  }

  get accountLabel(): string {
    return this.data?.accountLabel || (this.accountId ? `Account ${this.accountId}` : 'Account');
  }

  get accountOverviewLink(): string | null {
    return this.accountId ? `/accounts/account-manager/${this.accountId}/overview` : null;
  }

  private _persistDraftToTabContext(): void {
    const tabId = this.data?.tabId;
    if (!tabId) {
      return;
    }
    this._persistentDrawerTabsService.updateTabContext(tabId, {
      accountId: this.accountId,
      accountLabel: this.accountLabel,
      taskDraft: this.taskDraft,
    });
  }

  private _clearDraftFromTabContext(): void {
    const tabId = this.data?.tabId;
    if (!tabId) {
      return;
    }
    this._persistentDrawerTabsService.updateTabContext(tabId, {
      taskDraft: null,
    });
  }

  private _hydrateTaskDraft(draft: TaskCreateFormDraft | null | undefined): TaskCreateFormDraft | null {
    if (!draft) {
      return null;
    }
    return {
      ...draft,
      assign_to: Array.isArray(draft.assign_to) ? [...draft.assign_to] : undefined,
      team: Array.isArray(draft.team) ? [...draft.team] : undefined,
      account_user: Array.isArray(draft.account_user) ? [...draft.account_user] : undefined,
      task_related_entity: draft.task_related_entity ? { ...draft.task_related_entity } : null,
      qrAssociatedToTask: draft.qrAssociatedToTask ? { ...draft.qrAssociatedToTask } : null,
    };
  }

  private _draftEquals(
    left: TaskCreateFormDraft | null | undefined,
    right: TaskCreateFormDraft | null | undefined
  ): boolean {
    return JSON.stringify(this._hydrateTaskDraft(left)) === JSON.stringify(this._hydrateTaskDraft(right));
  }

  private _attachDraftSyncListener(): void {
    const tabId = this.data?.tabId;
    if (!tabId) {
      return;
    }

    this._tabs$
      .pipe(
        takeUntil(this._unsubscribeAll),
        map((tabs) => {
          const matchingTab = tabs.find((tab) => tab.id === tabId);
          return (matchingTab?.context?.['taskDraft'] as TaskCreateFormDraft | null | undefined) ?? null;
        }),
        distinctUntilChanged((previous, current) => this._draftEquals(previous, current))
      )
      .subscribe((draft) => {
        const nextDraft = this._hydrateTaskDraft(draft);
        if (this._draftEquals(this.taskDraft, nextDraft)) {
          return;
        }
        this.taskDraft = nextDraft;
      });
  }

  private _buildSelectedAccount(): Account {
    return {
      account_id: this.accountId ?? undefined,
      account_label: this.accountLabel,
      account_users: this.data?.accountUsers ?? [],
    } as Account;
  }
}
